import React from 'react';
import { LucideIcon } from 'lucide-react';

interface StatCardProps {
  title: string;
  value: string | number;
  change?: string;
  changeType?: 'positive' | 'negative' | 'neutral';
  icon: LucideIcon;
  color?: 'emerald' | 'blue' | 'purple' | 'orange' | 'red' | 'brand-primary' | 'brand-secondary' | 'brand-accent' | 'brand-mint' | 'brand-orange' | 'green';
}

const StatCard: React.FC<StatCardProps> = ({
  title,
  value,
  change,
  changeType = 'neutral',
  icon: Icon,
  color = 'emerald'
}) => {
  const colorClasses = {
    emerald: {
      bg: 'bg-emerald-50',
      icon: 'text-emerald-600',
      border: 'border-emerald-200',
      change: {
        positive: 'text-emerald-600',
        negative: 'text-red-600',
        neutral: 'text-gray-600'
      }
    },
    blue: {
      bg: 'bg-blue-50',
      icon: 'text-blue-600',
      border: 'border-blue-200',
      change: {
        positive: 'text-blue-600',
        negative: 'text-red-600',
        neutral: 'text-gray-600'
      }
    },
    purple: {
      bg: 'bg-purple-50',
      icon: 'text-purple-600',
      border: 'border-purple-200',
      change: {
        positive: 'text-purple-600',
        negative: 'text-red-600',
        neutral: 'text-gray-600'
      }
    },
    orange: {
      bg: 'bg-orange-50',
      icon: 'text-orange-600',
      border: 'border-orange-200',
      change: {
        positive: 'text-orange-600',
        negative: 'text-red-600',
        neutral: 'text-gray-600'
      }
    },
    red: {
      bg: 'bg-red-50',
      icon: 'text-red-600',
      border: 'border-red-200',
      change: {
        positive: 'text-red-600',
        negative: 'text-red-600',
        neutral: 'text-gray-600'
      }
    },
    'brand-primary': {
      bg: 'bg-brand-primary/10',
      icon: 'text-brand-primary',
      border: 'border-brand-primary/20',
      change: {
        positive: 'text-brand-primary',
        negative: 'text-red-600',
        neutral: 'text-gray-600'
      }
    },
    'brand-secondary': {
      bg: 'bg-brand-secondary/10',
      icon: 'text-brand-secondary',
      border: 'border-brand-secondary/20',
      change: {
        positive: 'text-brand-secondary',
        negative: 'text-red-600',
        neutral: 'text-gray-600'
      }
    },
    'brand-accent': {
      bg: 'bg-brand-accent/10',
      icon: 'text-brand-accent',
      border: 'border-brand-accent/20',
      change: {
        positive: 'text-brand-accent',
        negative: 'text-red-600',
        neutral: 'text-gray-600'
      }
    },
    'brand-mint': {
      bg: 'bg-brand-mint/10',
      icon: 'text-brand-mint',
      border: 'border-brand-mint/20',
      change: {
        positive: 'text-brand-mint',
        negative: 'text-red-600',
        neutral: 'text-gray-600'
      }
    },
    'brand-orange': {
      bg: 'bg-brand-orange/10',
      icon: 'text-brand-orange',
      border: 'border-brand-orange/20',
      change: {
        positive: 'text-brand-orange',
        negative: 'text-red-600',
        neutral: 'text-gray-600'
      }
    },
    green: {
      bg: 'bg-green-50',
      icon: 'text-green-600',
      border: 'border-green-200',
      change: {
        positive: 'text-green-600',
        negative: 'text-red-600',
        neutral: 'text-gray-600'
      }
    }
  };

  const classes = colorClasses[color];

  return (
    <div className={`bg-white rounded-xl border ${classes.border} p-6 shadow-sm hover:shadow-md transition-shadow`}>
      <div className="flex items-center justify-between">
        <div className="flex-1">
          <p className="text-sm font-medium text-gray-600 mb-1">{title}</p>
          <p className="text-2xl font-bold text-gray-900 mb-2">{value}</p>
          {change && (
            <div className="flex items-center space-x-1">
              <span className={`text-sm font-medium ${classes.change[changeType]}`}>
                {change}
              </span>
              {changeType === 'positive' && (
                <svg className="w-4 h-4 text-emerald-600" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M12 7a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0V8.414l-4.293 4.293a1 1 0 01-1.414 0L8 10.414l-4.293 4.293a1 1 0 01-1.414-1.414l5-5a1 1 0 011.414 0L12 10.586z" clipRule="evenodd" />
                </svg>
              )}
              {changeType === 'negative' && (
                <svg className="w-4 h-4 text-red-600" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M12 13a1 1 0 100 2h5a1 1 0 001-1v-5a1 1 0 10-2 0v2.586l-4.293-4.293a1 1 0 00-1.414 0L8 9.586l-4.293-4.293a1 1 0 00-1.414 1.414l5 5a1 1 0 001.414 0L12 13.586z" clipRule="evenodd" />
                </svg>
              )}
            </div>
          )}
        </div>
        <div className={`w-12 h-12 ${classes.bg} rounded-lg flex items-center justify-center`}>
          <Icon className={`h-6 w-6 ${classes.icon}`} />
        </div>
      </div>
    </div>
  );
};

export default StatCard;
